import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Trash2, Eye, Printer, Package, Truck, X, Search, ArrowLeft, Pencil, RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const statusLabels = { preparando: "Preparando", pronto: "Pronto p/ Envio", expedido: "Expedido" };
const statusColors = { preparando: "bg-yellow-100 text-yellow-800", pronto: "bg-blue-100 text-blue-800", expedido: "bg-green-100 text-green-800" };
const fmtBR = (v, decimals = 2) => (v || 0).toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export default function ExpeditionPage() {
  const { can } = useAuth();
  const isAdmin = can("expedicao", "edit");
  const [expeditions, setExpeditions] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "create" | "edit"
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expMaterials, setExpMaterials] = useState([]);
  const [editingExpId, setEditingExpId] = useState(null);

  // Create form state
  const [formClientId, setFormClientId] = useState("");
  const [formCompanyId, setFormCompanyId] = useState("");
  const [availableOrders, setAvailableOrders] = useState([]);
  const [formItems, setFormItems] = useState([]);
  const [formTransport, setFormTransport] = useState({ vehicle_plate: "", driver_name: "", carrier: "", notes: "" });
  const [formNotes, setFormNotes] = useState("");
  const [formExpCode, setFormExpCode] = useState("");
  const [addMode, setAddMode] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [companies, setCompanies] = useState([]);

  const fetchExpeditions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/expeditions`, { withCredentials: true });
      setExpeditions(data);
    } catch { toast.error("Erro ao carregar expedicoes"); }
    finally { setLoading(false); }
  }, []);

  const fetchClients = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/clients`, { withCredentials: true }); setClients(data); } catch (err) { console.error("fetchClients:", err); }
  }, []);

  useEffect(() => {
    fetchExpeditions();
    fetchClients();
    axios.get(`${API}/products`, { withCredentials: true }).then(({ data }) => setProducts(data)).catch((err) => console.error("fetchProducts:", err));
    axios.get(`${API}/companies`, { withCredentials: true }).then(({ data }) => setCompanies(data)).catch((err) => console.error("fetchCompanies:", err));
  }, [fetchExpeditions, fetchClients]);

  const handleClientChange = async (clientId) => {
    setFormClientId(clientId);
    setFormItems([]);
    if (!clientId) { setAvailableOrders([]); return; }
    try {
      const { data } = await axios.get(`${API}/orders/available-for-expedition?client_id=${clientId}`, { withCredentials: true });
      setAvailableOrders(data);
    } catch { setAvailableOrders([]); }
  };

  const addOrderItem = (order) => {
    const p = order.product; // product data from backend (may be null)
    const qty = order.available_quantity;
    setFormItems((prev) => [...prev, {
      order_id: order.id, order_code: order.order_code, order_title: order.title,
      product_name: order.title,
      drawing_number: p?.drawing_number || "",
      sku: p?.sku || "",
      sale_price: p?.sale_price || 0,
      weight: p?.weight || 0,
      height: p?.height || 0,
      width: p?.width || 0,
      length: p?.length || 0,
      quantity: qty, volume_number: 0, _max_qty: qty,
    }]);
  };

  const addProductItem = (product) => {
    setFormItems((prev) => [...prev, {
      order_id: "", order_code: "", order_title: "",
      product_name: product.name, drawing_number: product.drawing_number || "",
      sku: product.sku || "", sale_price: product.sale_price || 0,
      weight: product.weight || 0, height: product.height || 0,
      width: product.width || 0, length: product.length || 0,
      quantity: 1, volume_number: 0, _max_qty: null,
    }]);
    setAddMode(null);
    setProductSearch("");
  };

  const addManualItem = () => {
    setFormItems((prev) => [...prev, {
      order_id: "", order_code: "", order_title: "",
      product_name: "", drawing_number: "", sku: "",
      sale_price: 0, weight: 0, height: 0, width: 0, length: 0,
      quantity: 1, volume_number: 0, _max_qty: null,
    }]);
  };

  const updateItem = (idx, field, value) => {
    setFormItems((prev) => {
      const items = [...prev];
      items[idx] = { ...items[idx], [field]: value };
      return items;
    });
  };
  const removeItem = (idx) => setFormItems((prev) => prev.filter((_, i) => i !== idx));

  const openCreate = () => {
    setFormClientId(""); setFormCompanyId(""); setAvailableOrders([]); setFormItems([]);
    setFormTransport({ vehicle_plate: "", driver_name: "", carrier: "", notes: "" });
    setFormNotes(""); setFormExpCode(""); setAddMode(null); setProductSearch("");
    setEditingExpId(null);
    setView("create");
  };

  const openEdit = async (exp) => {
    setFormClientId(exp.client_id);
    setFormCompanyId(exp.company_id || "");
    setFormItems((exp.items || []).map((i) => ({
      ...i,
      _max_qty: null,
    })));
    setFormTransport(exp.transport || { vehicle_plate: "", driver_name: "", carrier: "", notes: "" });
    setFormNotes(exp.notes || "");
    setEditingExpId(exp.id);
    setAddMode(null);
    setProductSearch("");
    // Load available orders for this client
    try {
      const { data } = await axios.get(`${API}/orders/available-for-expedition?client_id=${exp.client_id}`, { withCredentials: true });
      setAvailableOrders(data);
    } catch { setAvailableOrders([]); }
    setDetailOpen(false);
    setView("edit");
  };

  const handleCreate = async (e) => {
    if (e) e.preventDefault();
    if (!formClientId) { toast.error("Selecione um cliente"); return; }
    if (!formItems.length) { toast.error("Adicione pelo menos um item"); return; }
    const payload = {
      client_id: formClientId,
      company_id: formCompanyId,
      expedition_code: formExpCode,
      items: formItems.map(({ _max_qty, id, ...rest }) => rest),
      transport: formTransport,
      notes: formNotes,
    };
    try {
      if (editingExpId) {
        // Update existing expedition
        await axios.put(`${API}/expeditions/${editingExpId}`, {
          items: payload.items,
          transport: payload.transport,
          notes: payload.notes,
          company_id: payload.company_id,
        }, { withCredentials: true });
        toast.success("Expedicao atualizada com sucesso");
      } else {
        // Create new
        await axios.post(`${API}/expeditions`, payload, { withCredentials: true });
        toast.success("Expedicao criada com sucesso");
      }
      setView("list");
      setEditingExpId(null);
      fetchExpeditions();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar expedicao"); }
  };

  const openDetail = async (exp) => {
    try {
      const [expRes, matsRes] = await Promise.all([
        axios.get(`${API}/expeditions/${exp.id}`, { withCredentials: true }),
        axios.get(`${API}/expeditions/${exp.id}/materials`, { withCredentials: true }),
      ]);
      setSelectedExp(expRes.data);
      setExpMaterials(matsRes.data);
      setDetailOpen(true);
    } catch { toast.error("Erro ao carregar expedicao"); }
  };

  const handleStatusChange = async (expId, newStatus) => {
    try {
      const { data } = await axios.patch(`${API}/expeditions/${expId}/status`, { status: newStatus }, { withCredentials: true });
      setSelectedExp(data); fetchExpeditions();
      toast.success(`Status atualizado para ${statusLabels[newStatus]}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar status"); }
  };

  const handleDelete = async () => {
    if (!selectedExp) return;
    try {
      await axios.delete(`${API}/expeditions/${selectedExp.id}`, { withCredentials: true });
      toast.success("Expedicao removida"); setDetailOpen(false); setSelectedExp(null); fetchExpeditions();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao excluir"); }
  };

  const handleRecalculate = async () => {
    if (!selectedExp) return;
    try {
      const { data } = await axios.post(`${API}/expeditions/${selectedExp.id}/recalculate`, {}, { withCredentials: true });
      setSelectedExp(data.expedition);
      fetchExpeditions();
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao recalcular"); }
  };

  const handlePrint = () => {
    if (!selectedExp) return;
    const e = selectedExp;
    const items = e.items || [];
    // Calculate total price
    const totalPrice = items.reduce((acc, i) => acc + (i.sale_price || 0) * (i.quantity || 0), 0);
    const totalVolumes = items.reduce((acc, i) => acc + (i.volume_number || 0), 0);
    // Items table (no grouping - volume is a quantity per item)
    const rows = items.map((i) => {
      const cub = (i.height && i.width && i.length) ? `${i.height}x${i.width}x${i.length}m` : "-";
      return `<tr><td>${i.product_name}</td><td>${i.drawing_number || "-"}</td><td>${i.sku || "-"}</td><td style="text-align:center">${i.volume_number || 0}</td><td style="text-align:right">${i.quantity}</td><td style="text-align:right">R$ ${fmtBR(i.sale_price || 0)}</td><td style="text-align:right">R$ ${fmtBR((i.sale_price || 0) * (i.quantity || 0))}</td><td>${i.weight ? fmtBR(i.weight) + " kg" : "-"}</td><td>${cub}</td></tr>`;
    }).join("");
    const itemsHtml = `<table><thead><tr><th>Produto</th><th>Desenho</th><th>Ordem Fab.</th><th style="text-align:center">Volumes</th><th>Qtd</th><th>Preco Unit.</th><th>Subtotal</th><th>Peso Unit.</th><th>Cubagem</th></tr></thead><tbody>${rows}</tbody></table>`;
    const companyHtml = e.company_name ? `<div style="margin-bottom:16px;padding:10px;border:1px solid #ddd;border-radius:4px"><div style="font-size:9px;text-transform:uppercase;color:#888">Empresa Emissora</div><div style="font-size:14px;font-weight:bold;margin-top:2px">${e.company_name}</div>${e.company_cnpj ? `<div style="font-size:11px;color:#555;font-family:monospace">${e.company_cnpj}</div>` : ""}${e.company_address || e.company_city ? `<div style="font-size:11px;color:#555">${[e.company_address, e.company_city, e.company_state].filter(Boolean).join(", ")}${e.company_cep ? ` - CEP ${e.company_cep}` : ""}</div>` : ""}${e.company_phone || e.company_email ? `<div style="font-size:11px;color:#555">${[e.company_phone, e.company_email].filter(Boolean).join(" | ")}</div>` : ""}</div>` : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Expedicao ${e.expedition_code}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:12px;padding:20px;color:#222}.header{display:flex;justify-content:space-between;border-bottom:2px solid #333;padding-bottom:12px;margin-bottom:16px}.header h1{font-size:18px}.header .code{font-size:14px;color:#555;font-family:monospace}.header .right{text-align:right;font-size:11px;color:#555}.info-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:12px;margin-bottom:16px;padding:10px;background:#f7f7f7;border-radius:4px}.info-grid .label{font-size:9px;text-transform:uppercase;color:#888}.info-grid .value{font-size:12px;font-weight:bold;margin-top:2px}h2{font-size:14px;margin:16px 0 8px;border-bottom:1px solid #ddd;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th{background:#f0f0f0;text-align:left;padding:6px 8px;font-size:10px;text-transform:uppercase;border-bottom:2px solid #ddd}td{padding:6px 8px;border-bottom:1px solid #eee}.totals{margin-top:16px;padding:10px;background:#f7f7f7;border-radius:4px}.receipt{margin-top:32px;padding:16px;border:1px solid #333;border-radius:4px;page-break-inside:avoid}.receipt h3{font-size:13px;margin-bottom:10px;text-transform:uppercase;letter-spacing:0.5px}.receipt .disclaimer{font-size:10px;color:#555;margin-bottom:16px;line-height:1.4}.receipt-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px 24px;margin-bottom:20px}.receipt-field{display:flex;flex-direction:column}.receipt-field .field-label{font-size:9px;text-transform:uppercase;color:#666;margin-bottom:2px}.receipt-field .field-line{border-bottom:1px solid #333;min-height:22px}.signature-block{margin-top:24px;text-align:center}.signature-block .sig-line{border-bottom:1px solid #333;margin:0 auto 4px;width:70%;min-height:40px}.signature-block .sig-label{font-size:10px;color:#333;text-transform:uppercase;letter-spacing:0.3px}.footer{margin-top:24px;padding-top:12px;border-top:1px solid #ddd;font-size:10px;color:#888;text-align:center}@media print{body{padding:10px}.receipt{break-inside:avoid;page-break-inside:avoid}}</style></head><body>
    <div class="header"><div><h1>Ordem de Expedicao</h1><div class="code">${e.expedition_code}</div></div><div class="right"><div>${e.company_name || "Liga Facil - PCP"}</div><div>Impresso em: ${format(new Date(), "dd/MM/yyyy HH:mm")}</div></div></div>
    ${companyHtml}
    <div class="info-grid"><div><div class="label">Cliente</div><div class="value">${e.client_code ? e.client_code + " - " : ""}${e.client_name}</div></div><div><div class="label">Status</div><div class="value">${statusLabels[e.status] || e.status}</div></div><div><div class="label">Data</div><div class="value">${format(new Date(e.created_at), "dd/MM/yyyy")}</div></div><div><div class="label">Itens</div><div class="value">${e.total_items || 0}</div></div><div><div class="label">Volumes</div><div class="value">${totalVolumes}</div></div><div><div class="label">Peso Total</div><div class="value">${fmtBR(e.total_weight || 0)} kg</div></div><div><div class="label">Valor Total</div><div class="value" style="color:#1a56db">R$ ${fmtBR(totalPrice)}</div></div></div>
    ${e.transport?.carrier || e.transport?.vehicle_plate || e.transport?.driver_name ? `<h2>Transporte</h2><div class="info-grid"><div><div class="label">Transportadora</div><div class="value">${e.transport.carrier || "-"}</div></div><div><div class="label">Placa</div><div class="value">${e.transport.vehicle_plate || "-"}</div></div><div><div class="label">Motorista</div><div class="value">${e.transport.driver_name || "-"}</div></div></div>` : ""}
    <h2>Itens da Expedicao</h2>${itemsHtml}
    <div class="totals"><strong>Resumo:</strong> ${e.total_items || 0} itens | ${totalVolumes} volumes | Peso total: ${fmtBR(e.total_weight || 0)} kg | Cubagem total: ${fmtBR(e.total_volume_m3 || 0, 4)} m3 | <strong>Valor total: R$ ${fmtBR(totalPrice)}</strong></div>
    ${expMaterials.length > 0 ? `<h2 style="margin-top:24px">Materiais Utilizados</h2><table><thead><tr><th>Codigo</th><th>Material</th><th>Unidade</th><th style="text-align:right">Qtd Prevista</th><th style="text-align:right">Qtd Confirmada</th></tr></thead><tbody>${expMaterials.map(m => `<tr><td style="font-family:monospace">${m.material_code || "-"}</td><td>${m.material_name || "-"}</td><td>${m.unit || "-"}</td><td style="text-align:right">${fmtBR(m.planned_quantity)}</td><td style="text-align:right">${fmtBR(m.confirmed_quantity)}</td></tr>`).join("")}</tbody></table>` : ""}
    ${e.notes ? `<h2>Observacoes</h2><p>${e.notes}</p>` : ""}
    <div class="receipt">
      <h3>Comprovante de Recebimento</h3>
      <div class="disclaimer">Declaro para os devidos fins que recebi os materiais/produtos discriminados neste romaneio, nas quantidades e condicoes descritas acima, sem avarias aparentes.</div>
      <div class="receipt-grid">
        <div class="receipt-field"><span class="field-label">Nome do Recebedor</span><span class="field-line"></span></div>
        <div class="receipt-field"><span class="field-label">Documento (RG / CPF)</span><span class="field-line"></span></div>
        <div class="receipt-field"><span class="field-label">Data do Recebimento</span><span class="field-line"></span></div>
        <div class="receipt-field"><span class="field-label">Hora</span><span class="field-line"></span></div>
      </div>
      <div class="signature-block">
        <div class="sig-line"></div>
        <div class="sig-label">Assinatura do Recebedor</div>
      </div>
    </div>
    <div class="footer">Liga Facil - Sistema de Planejamento e Controle de Producao</div>
    <script>window.onload=function(){window.print()}</script></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (w) w.onload = () => URL.revokeObjectURL(url);
  };

  const filtered = expeditions.filter((e) => {
    if (filterStatus !== "all" && e.status !== filterStatus) return false;
    if (filterClient !== "all" && e.client_id !== filterClient) return false;
    return true;
  });

  const formTotalWeight = formItems.reduce((acc, i) => acc + (i.weight || 0) * (i.quantity || 0), 0);
  const formTotalVolume = formItems.reduce((acc, i) => (i.height && i.width && i.length) ? acc + i.height * i.width * i.length * (i.quantity || 0) : acc, 0);
  const formTotalItems = formItems.reduce((acc, i) => acc + (i.quantity || 0), 0);
  const formTotalVolumes = formItems.reduce((acc, i) => acc + (i.volume_number || 0), 0);
  const formTotalPrice = formItems.reduce((acc, i) => acc + (i.sale_price || 0) * (i.quantity || 0), 0);

  const filteredProducts = productSearch.trim()
    ? products.filter((p) => (p.name || "").toLowerCase().includes(productSearch.toLowerCase()) || (p.code || "").toLowerCase().includes(productSearch.toLowerCase())).slice(0, 8)
    : products.slice(0, 8);

  // ─── CREATE VIEW (Full Page) ───
  if (view === "create" || view === "edit") {
    const isEditing = view === "edit";
    return (
      <div className="animate-slide-in" data-testid="expedition-create-view">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setView("list"); setEditingExpId(null); }} data-testid="back-to-list-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight">{isEditing ? "Editar Ordem de Expedicao" : "Nova Ordem de Expedicao"}</h1>
              <p className="text-xs text-muted-foreground">{isEditing ? "Altere itens, transporte e observacoes" : "Selecione cliente, adicione itens e dados de transporte"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live totals in top bar */}
            <div className="hidden md:flex items-center gap-4 text-xs bg-muted/40 px-4 py-2 rounded-lg">
              <span><strong>{formTotalItems}</strong> itens</span>
              <span><strong>{formTotalVolumes}</strong> vol</span>
              <span><strong>{fmtBR(formTotalWeight, 1)}</strong> kg</span>
              <span><strong>{fmtBR(formTotalVolume, 3)}</strong> m3</span>
              <span className="font-semibold text-primary">R$ {fmtBR(formTotalPrice)}</span>
            </div>
            <Button variant="outline" onClick={() => { setView("list"); setEditingExpId(null); }}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={!formClientId || !formItems.length} data-testid="exp-submit-btn">
              <Truck className="h-4 w-4 mr-2" /> {isEditing ? "Salvar Alteracoes" : "Criar Expedicao"}
            </Button>
          </div>
        </div>

        {/* Main layout: 2 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" style={{ height: "calc(100vh - 160px)" }}>
          {/* LEFT PANEL: Client + Orders + Sources */}
          <div className="flex flex-col gap-3 overflow-y-auto pr-1">
            {/* Code + Client */}
            <Card className="border shadow-none">
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <div className="space-y-1">
                    <Label className="font-semibold text-xs uppercase text-muted-foreground">Codigo</Label>
                    <Input value={formExpCode} onChange={(e) => setFormExpCode(e.target.value)}
                      placeholder="Auto" className="h-9 font-mono text-xs" disabled={isEditing}
                      data-testid="exp-code-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="font-semibold text-xs uppercase text-muted-foreground">Cliente</Label>
                    <Select value={formClientId} onValueChange={handleClientChange} disabled={isEditing}>
                      <SelectTrigger data-testid="exp-client-select"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground">Deixe vazio para gerar automaticamente (EXP-001, EXP-002...)</p>
              </CardContent>
            </Card>

            {/* Company */}
            {companies.length > 0 && (
              <Card className="border shadow-none">
                <CardContent className="p-3 space-y-2">
                  <Label className="font-semibold text-xs uppercase text-muted-foreground">Empresa Emissora</Label>
                  <Select value={formCompanyId} onValueChange={setFormCompanyId}>
                    <SelectTrigger data-testid="exp-company-select"><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Available Orders */}
            {formClientId && (
              <Card className="border shadow-none flex-1 min-h-0">
                <CardContent className="p-3 space-y-2 h-full flex flex-col">
                  <Label className="font-semibold text-xs uppercase text-muted-foreground">Ordens Finalizadas</Label>
                  {availableOrders.length === 0 ? (
                    <div className="text-xs text-muted-foreground text-center py-4">Nenhuma ordem finalizada disponivel</div>
                  ) : (
                    <div className="flex-1 overflow-y-auto space-y-1.5">
                      {availableOrders.map((o) => {
                        const alreadyAdded = formItems.some((fi) => fi.order_id === o.id);
                        return (
                          <div key={o.id} className={`flex items-start justify-between p-2 rounded border text-xs ${alreadyAdded ? "bg-green-50 border-green-200" : "bg-white hover:bg-muted/30 cursor-pointer"}`}
                            onClick={() => !alreadyAdded && addOrderItem(o)} data-testid={`add-order-${o.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="font-mono font-semibold">{o.order_code || "-"}</div>
                              <div className="text-muted-foreground truncate">{o.title}</div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-muted-foreground">Disp: <strong>{o.available_quantity}</strong>/{o.quantity}</span>
                                {o.product && <Badge variant="outline" className="text-[9px]">{o.product.code || o.product.name}</Badge>}
                              </div>
                            </div>
                            {alreadyAdded ? (
                              <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0 ml-2">Adicionado</Badge>
                            ) : (
                              <Plus className="h-3.5 w-3.5 text-primary shrink-0 mt-1 ml-2" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Add from product or manual */}
            {formClientId && (
              <Card className="border shadow-none">
                <CardContent className="p-3 space-y-2">
                  <Label className="font-semibold text-xs uppercase text-muted-foreground">Adicionar Item</Label>
                  <div className="flex gap-1">
                    <Button type="button" variant={addMode === "product" ? "default" : "outline"} size="sm" className="flex-1 text-xs"
                      onClick={() => setAddMode(addMode === "product" ? null : "product")} data-testid="add-product-item-btn">
                      <Package className="h-3 w-3 mr-1" /> Produto
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="flex-1 text-xs"
                      onClick={addManualItem} data-testid="add-manual-item-btn">
                      <Plus className="h-3 w-3 mr-1" /> Manual
                    </Button>
                  </div>
                  {addMode === "product" && (
                    <div className="space-y-1.5">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input placeholder="Buscar produto..." value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)} className="h-8 text-xs pl-7" autoFocus data-testid="product-search-input" />
                      </div>
                      <div className="max-h-[150px] overflow-y-auto space-y-1">
                        {filteredProducts.map((p) => (
                          <div key={p.id} className="flex items-center justify-between px-2 py-1.5 bg-white rounded border text-xs cursor-pointer hover:bg-muted/30"
                            onClick={() => addProductItem(p)} data-testid={`pick-product-${p.id}`}>
                            <div className="truncate flex-1"><span className="font-mono mr-1">{p.code || ""}</span>{p.name}</div>
                            <span className="text-muted-foreground ml-2 shrink-0">{p.sale_price ? `R$ ${fmtBR(p.sale_price)}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Transport */}
            {formClientId && (
              <Card className="border shadow-none">
                <CardContent className="p-3 space-y-2">
                  <Label className="font-semibold text-xs uppercase text-muted-foreground">Transporte</Label>
                  <Input placeholder="Transportadora" value={formTransport.carrier}
                    onChange={(e) => setFormTransport({ ...formTransport, carrier: e.target.value })}
                    className="h-8 text-xs" data-testid="exp-carrier" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Placa" value={formTransport.vehicle_plate}
                      onChange={(e) => setFormTransport({ ...formTransport, vehicle_plate: e.target.value })}
                      className="h-8 text-xs" data-testid="exp-plate" />
                    <Input placeholder="Motorista" value={formTransport.driver_name}
                      onChange={(e) => setFormTransport({ ...formTransport, driver_name: e.target.value })}
                      className="h-8 text-xs" data-testid="exp-driver" />
                  </div>
                  <Textarea placeholder="Observacoes..." value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)} rows={2} className="text-xs resize-none" data-testid="exp-notes" />
                </CardContent>
              </Card>
            )}
          </div>

          {/* RIGHT PANEL: Items */}
          <Card className="border shadow-none overflow-hidden flex flex-col">
            <CardContent className="p-0 flex-1 flex flex-col min-h-0">
              {formItems.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                  <div className="text-center">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Selecione um cliente e adicione itens</p>
                    <p className="text-xs mt-1">Clique nas ordens finalizadas ou use os botoes Produto/Manual</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto p-3 space-y-3">
                    {formItems.map((item, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-card relative group" data-testid={`exp-item-${idx}`}>
                        <Button type="button" variant="ghost" size="icon"
                          className="absolute top-2 right-2 h-6 w-6 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeItem(idx)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>

                        {/* Row 1: Product name + Volume */}
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2 bg-primary/10 rounded px-3 py-1.5 shrink-0">
                            <Package className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold text-primary">Volumes</span>
                            <Input type="number" min={0} value={item.volume_number}
                              onChange={(e) => updateItem(idx, "volume_number", parseInt(e.target.value) || 0)}
                              className="h-7 text-sm w-16 text-center font-bold border-primary/30" data-testid={`item-vol-${idx}`} />
                          </div>
                          <Input value={item.product_name} onChange={(e) => updateItem(idx, "product_name", e.target.value)}
                            className="h-8 text-sm font-medium flex-1" placeholder="Nome do produto" data-testid={`item-name-${idx}`} />
                          {item.order_code && (
                            <Badge variant="outline" className="text-[10px] shrink-0">Ordem: {item.order_code}</Badge>
                          )}
                        </div>

                        {/* Row 2: Details grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Desenho</Label>
                            <Input value={item.drawing_number} onChange={(e) => updateItem(idx, "drawing_number", e.target.value)}
                              className="h-7 text-xs" placeholder="-" data-testid={`item-drawing-${idx}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Ordem Fabricacao</Label>
                            <Input value={item.sku} onChange={(e) => updateItem(idx, "sku", e.target.value)}
                              className="h-7 text-xs" placeholder="-" data-testid={`item-sku-${idx}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Quantidade</Label>
                            <Input type="number" min={1} max={item._max_qty || undefined} value={item.quantity}
                              onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs" data-testid={`item-qty-${idx}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Preco (R$)</Label>
                            <Input type="number" step="0.01" min={0} value={item.sale_price}
                              onChange={(e) => updateItem(idx, "sale_price", parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs" data-testid={`item-price-${idx}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Peso (kg)</Label>
                            <Input type="number" step="0.01" min={0} value={item.weight}
                              onChange={(e) => updateItem(idx, "weight", parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs" data-testid={`item-weight-${idx}`} />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Cubagem (AxLxC m)</Label>
                            <div className="flex gap-1">
                              <Input type="number" step="0.01" min={0} value={item.height} placeholder="A"
                                onChange={(e) => updateItem(idx, "height", parseFloat(e.target.value) || 0)}
                                className="h-7 text-xs flex-1" data-testid={`item-h-${idx}`} />
                              <Input type="number" step="0.01" min={0} value={item.width} placeholder="L"
                                onChange={(e) => updateItem(idx, "width", parseFloat(e.target.value) || 0)}
                                className="h-7 text-xs flex-1" data-testid={`item-w-${idx}`} />
                              <Input type="number" step="0.01" min={0} value={item.length} placeholder="C"
                                onChange={(e) => updateItem(idx, "length", parseFloat(e.target.value) || 0)}
                                className="h-7 text-xs flex-1" data-testid={`item-l-${idx}`} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Bottom totals bar */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 border-t text-xs shrink-0">
                    <div className="flex items-center gap-5">
                      <span><strong>{formTotalItems}</strong> itens</span>
                      <span><strong>{formTotalVolumes}</strong> volumes</span>
                      <span>Peso: <strong>{fmtBR(formTotalWeight)} kg</strong></span>
                      <span>Cubagem: <strong>{fmtBR(formTotalVolume, 4)} m3</strong></span>
                    </div>
                    <span className="font-semibold text-sm text-primary">Total: R$ {fmtBR(formTotalPrice)}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Mobile totals */}
        <div className="md:hidden flex items-center justify-between mt-3 p-3 bg-muted/40 rounded-lg text-xs">
          <div className="flex items-center gap-3">
            <span><strong>{formTotalItems}</strong> itens</span>
            <span><strong>{formTotalVolumes}</strong> vol</span>
            <span><strong>{fmtBR(formTotalWeight, 1)}</strong> kg</span>
          </div>
          <span className="font-semibold text-primary">R$ {fmtBR(formTotalPrice)}</span>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div className="space-y-6 animate-slide-in" data-testid="expedition-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Expedicao</h1>
          <p className="text-sm text-muted-foreground mt-1">Gerencie as ordens de expedicao e romaneios</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="exp-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="preparando">Preparando</SelectItem>
              <SelectItem value="pronto">Pronto p/ Envio</SelectItem>
              <SelectItem value="expedido">Expedido</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-48" data-testid="exp-client-filter"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os clientes</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {can("expedicao", "create") && (
          <Button onClick={openCreate} data-testid="create-expedition-btn">
            <Plus className="h-4 w-4 mr-2" /> Nova Expedicao
          </Button>
          )}
        </div>
      </div>

      <Card className="border border-border shadow-none overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[100px]">Codigo</TableHead>
                  <TableHead className="min-w-[180px]">Cliente</TableHead>
                  <TableHead className="min-w-[100px]">Status</TableHead>
                  <TableHead className="min-w-[80px]">Itens</TableHead>
                  <TableHead className="min-w-[80px]">Volumes</TableHead>
                  <TableHead className="min-w-[100px]">Peso Total</TableHead>
                  <TableHead className="min-w-[100px]">Data</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Nenhuma expedicao encontrada</TableCell></TableRow>
                ) : (
                  filtered.map((exp) => (
                    <TableRow key={exp.id} className="cursor-pointer" onClick={() => openDetail(exp)} data-testid={`exp-row-${exp.id}`}>
                      <TableCell className="font-mono text-xs font-semibold">{exp.expedition_code}</TableCell>
                      <TableCell className="text-sm">{exp.client_code ? `${exp.client_code} - ` : ""}{exp.client_name}</TableCell>
                      <TableCell><Badge className={statusColors[exp.status]}>{statusLabels[exp.status]}</Badge></TableCell>
                      <TableCell className="text-sm">{exp.total_items || 0}</TableCell>
                      <TableCell className="text-sm">{exp.total_volumes || 0}</TableCell>
                      <TableCell className="text-sm">{exp.total_weight ? `${fmtBR(exp.total_weight)} kg` : "-"}</TableCell>
                      <TableCell className="text-sm">{format(new Date(exp.created_at), "dd/MM/yyyy")}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8"><Eye className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={(v) => { setDetailOpen(v); if (!v) setSelectedExp(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedExp && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-sm text-muted-foreground">{selectedExp.expedition_code}</span>
                  Ordem de Expedicao
                  <Badge className={statusColors[selectedExp.status]}>{statusLabels[selectedExp.status]}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {selectedExp.company_name && <span className="font-semibold">{selectedExp.company_name} | </span>}
                  Cliente: {selectedExp.client_code ? `${selectedExp.client_code} - ` : ""}{selectedExp.client_name} | Criado por: {selectedExp.created_by}
                </DialogDescription>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-exp-btn">
                    <Printer className="h-3.5 w-3.5 mr-1" /> Imprimir
                  </Button>
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => openEdit(selectedExp)} data-testid="edit-exp-btn">
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={handleRecalculate} data-testid="recalc-exp-btn">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Recalcular
                    </Button>
                  )}
                  <Select value={selectedExp.status} onValueChange={(v) => handleStatusChange(selectedExp.id, v)} disabled={!isAdmin}>
                    <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="exp-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preparando">Preparando</SelectItem>
                      <SelectItem value="pronto">Pronto p/ Envio</SelectItem>
                      <SelectItem value="expedido">Expedido</SelectItem>
                    </SelectContent>
                  </Select>
                  {can("expedicao", "delete") && (
                    <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => setDeleteConfirmOpen(true)} data-testid="delete-exp-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir
                    </Button>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-5">
                {/* Summary with price */}
                {(() => {
                  const detailTotalPrice = (selectedExp.items || []).reduce((acc, i) => acc + (i.sale_price || 0) * (i.quantity || 0), 0);
                  const detailTotalVolumes = (selectedExp.items || []).reduce((acc, i) => acc + (i.volume_number || 0), 0);
                  return (
                    <div className="grid grid-cols-5 gap-4 text-sm">
                      <div><span className="text-xs text-muted-foreground uppercase">Total Itens</span><p className="mt-1 font-medium text-lg">{selectedExp.total_items || 0}</p></div>
                      <div><span className="text-xs text-muted-foreground uppercase">Volumes</span><p className="mt-1 font-medium text-lg">{detailTotalVolumes}</p></div>
                      <div><span className="text-xs text-muted-foreground uppercase">Peso Total</span><p className="mt-1 font-medium">{fmtBR(selectedExp.total_weight || 0)} kg</p></div>
                      <div><span className="text-xs text-muted-foreground uppercase">Cubagem Total</span><p className="mt-1 font-medium">{fmtBR(selectedExp.total_volume_m3 || 0, 4)} m3</p></div>
                      <div><span className="text-xs text-muted-foreground uppercase">Valor Total</span><p className="mt-1 font-medium text-lg text-primary">R$ {fmtBR(detailTotalPrice)}</p></div>
                    </div>
                  );
                })()}

                {(selectedExp.transport?.carrier || selectedExp.transport?.vehicle_plate || selectedExp.transport?.driver_name) && (
                  <div>
                    <h3 className="font-heading font-semibold text-sm mb-2">Transporte</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div><span className="text-xs text-muted-foreground">Transportadora</span><p className="font-medium">{selectedExp.transport.carrier || "-"}</p></div>
                      <div><span className="text-xs text-muted-foreground">Placa</span><p className="font-medium">{selectedExp.transport.vehicle_plate || "-"}</p></div>
                      <div><span className="text-xs text-muted-foreground">Motorista</span><p className="font-medium">{selectedExp.transport.driver_name || "-"}</p></div>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-heading font-semibold text-sm mb-2">Itens ({(selectedExp.items || []).length})</h3>
                  <Table>
                    <TableHeader><TableRow className="text-xs">
                      <TableHead className="py-1.5">Produto</TableHead><TableHead className="py-1.5">Desenho</TableHead>
                      <TableHead className="py-1.5">Ordem Fab.</TableHead><TableHead className="py-1.5 text-center">Vol</TableHead>
                      <TableHead className="py-1.5">Qtd</TableHead><TableHead className="py-1.5">Preco Unit.</TableHead>
                      <TableHead className="py-1.5">Peso Unit.</TableHead><TableHead className="py-1.5">Cubagem</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(selectedExp.items || []).map((item) => (
                        <TableRow key={item.id} className="text-sm">
                          <TableCell className="py-1.5">{item.product_name}</TableCell>
                          <TableCell className="py-1.5 text-xs">{item.drawing_number || "-"}</TableCell>
                          <TableCell className="py-1.5 text-xs font-mono">{item.sku || "-"}</TableCell>
                          <TableCell className="py-1.5 text-center">{item.volume_number || 0}</TableCell>
                          <TableCell className="py-1.5">{item.quantity}</TableCell>
                          <TableCell className="py-1.5 text-xs">R$ {fmtBR(item.sale_price || 0)}</TableCell>
                          <TableCell className="py-1.5 text-xs">{item.weight ? `${item.weight} kg` : "-"}</TableCell>
                          <TableCell className="py-1.5 text-xs">{item.height && item.width && item.length ? `${item.height}x${item.width}x${item.length}m` : "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Materiais Utilizados */}
                {expMaterials.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold text-sm mb-2">Materiais Utilizados</h3>
                    <Table>
                      <TableHeader><TableRow className="text-xs">
                        <TableHead className="py-1.5">Codigo</TableHead>
                        <TableHead className="py-1.5">Material</TableHead>
                        <TableHead className="py-1.5">Unidade</TableHead>
                        <TableHead className="py-1.5 text-right">Qtd Prevista</TableHead>
                        <TableHead className="py-1.5 text-right">Qtd Confirmada</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {expMaterials.map((mat, idx) => (
                          <TableRow key={mat.material_id || mat.material_code || idx} className="text-sm">
                            <TableCell className="py-1.5 font-mono text-xs">{mat.material_code || "-"}</TableCell>
                            <TableCell className="py-1.5">{mat.material_name || "-"}</TableCell>
                            <TableCell className="py-1.5 text-xs">{mat.unit || "-"}</TableCell>
                            <TableCell className="py-1.5 text-right">{fmtBR(mat.planned_quantity)}</TableCell>
                            <TableCell className="py-1.5 text-right">{fmtBR(mat.confirmed_quantity)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {selectedExp.notes && (
                  <div><h3 className="font-heading font-semibold text-sm mb-1">Observacoes</h3><p className="text-sm text-muted-foreground">{selectedExp.notes}</p></div>
                )}

                <div className="text-xs text-muted-foreground">
                  Criado em {format(new Date(selectedExp.created_at), "dd/MM/yyyy HH:mm")}
                  {selectedExp.expedited_at && ` | Expedido em ${format(new Date(selectedExp.expedited_at), "dd/MM/yyyy HH:mm")} por ${selectedExp.expedited_by}`}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusao</DialogTitle>
            <DialogDescription>Deseja realmente excluir esta expedicao? As quantidades expedidas serao devolvidas as ordens.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => { handleDelete(); setDeleteConfirmOpen(false); }} data-testid="confirm-delete-exp">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
